/**
 * Messages that left a composer and were never stored, held OUTSIDE any component.
 *
 * Both support surfaces clear the composer at send, so what is in flight is the only
 * copy — and both can be taken off the screen underneath it. The console unmounts on
 * sidebar navigation, the browser's back button and any route change; the widget goes
 * with the whole authenticated app when a session expires, since the API answers the
 * write with "sign in again" and the login screen replaces everything. Component state
 * dies in either case, and the person is left believing they sent something.
 *
 * The rules below were each paid for by a defect, and they are collected here so the two
 * surfaces cannot drift apart on them again — the widget's own copy of this idea
 * reintroduced three of them.
 *
 *  1. A send is recorded BEFORE its request is awaited, and removed only when the server
 *     confirms it. Recording on failure assumed every send eventually fails or succeeds,
 *     and some never do.
 *
 *  2. Each scope holds a QUEUE, oldest first. One slot per scope loses messages: with A
 *     already held, B failing must not overwrite it — both are unfinished work.
 *
 *  3. An entry still `inFlight` is never offered back. A request outlives the component
 *     that made it, so it can still commit; handing it back invites an edit, and the
 *     edit would go out under a fresh id while the original landed under its own.
 *
 *  4. Restoring is not confirmation. A message handed back into a composer stays
 *     recorded until the server takes it or the sender replaces it, because composers
 *     die with the page and the request behind an adopted entry may never settle.
 *
 *  5. The scope names WHO as well as what. This is module state that outlives a session,
 *     and the next person to sign in on the browser must not be handed a stranger's
 *     words.
 *
 * The record carries its `clientMessageId`, so a send that COMMITTED but lost its
 * response is re-sent under the SAME id and recognised by the server as a duplicate
 * rather than stored twice. (A closed TAB still loses the draft — nothing client-side
 * survives that — but the id makes the loss a draft, never a duplicate.)
 */

/** scope -> entries, oldest first. Exported so tests can drain it between cases. */
export const heldSendsByScope = new Map();

/**
 * Surfaces that want to hear about a change they did not cause.
 *
 * A send settles through the promise chain of the component that started it, which may
 * be gone. The page on screen then has a message waiting for it and no reason to look.
 */
const listeners = new Set();

export const onHeldSendsChanged = (listen) => {
  listeners.add(listen);
  return () => listeners.delete(listen);
};

const announce = () => {
  listeners.forEach((listen) => listen());
};

/**
 * Record a send as it goes out. Returns the entry, which the caller settles or drops.
 *
 * The registry key is stored as `key`, deliberately not `scope`: the widget already uses
 * `scope` for something else — the identity a retry is judged against — and overwriting
 * it made every restored message go out under a fresh client id, which is the one thing
 * the record exists to prevent.
 */
export const recordSend = (key, send) => {
  const entry = { ...send, key, inFlight: true };
  const queue = heldSendsByScope.get(key) || [];
  queue.push(entry);
  heldSendsByScope.set(key, queue);
  return entry;
};

/**
 * Forget one entry, by identity.
 *
 * Never by `clientMessageId`: two entries can share one — a restored message re-sent is
 * the same message — and forgetting the wrong one loses work.
 */
export const dropSend = (entry) => {
  if (!entry) return;
  const queue = heldSendsByScope.get(entry.key);
  if (!queue) return;
  const at = queue.indexOf(entry);
  if (at >= 0) queue.splice(at, 1);
  if (!queue.length) heldSendsByScope.delete(entry.key);
};

/**
 * Mark an entry's request as finished, so it may be offered back.
 *
 * `orphaned` says the component that started it is gone, which is what makes this worth
 * announcing: nothing else is going to notice.
 */
export const settleSend = (entry, { orphaned } = {}) => {
  if (!entry) return;
  entry.inFlight = false;
  if (orphaned) announce();
};

/**
 * Forget every entry whose message the SERVER can be seen to hold.
 *
 * Both surfaces worked out this set for themselves and edited `heldSendsByScope` in place.
 * That is how a queued message came to be stranded: with A held behind B, retiring B
 * removed it from the queue and cleared it from the composer, but nothing told the
 * surfaces the registry had changed — so A, now at the front and offerable, was never
 * handed back. It survived only in module memory, invisible, and a reload or a closed tab
 * lost it for good.
 *
 * So the edit lives here, with the announcement, and both surfaces get the restoration
 * they already subscribe to. Returns whether anything was actually retired.
 */
export const retireStoredSends = (storedIds) => {
  if (!storedIds || !storedIds.size) return false;

  let changed = false;
  [...heldSendsByScope.keys()].forEach((key) => {
    const queue = heldSendsByScope.get(key) || [];
    const keep = queue.filter((entry) => !storedIds.has(entry.clientMessageId));
    if (keep.length === queue.length) return;
    changed = true;
    // The key is restated on the survivors for the same reason `recordSend` sets it:
    // an entry that loses it goes back out under a fresh client id.
    keep.forEach((entry) => { entry.key = key; });
    if (keep.length) heldSendsByScope.set(key, keep);
    else heldSendsByScope.delete(key);
  });

  // Announced AFTER every scope is settled, so a listener that restores sees the final
  // state rather than one with a retired entry still in front of what it should offer.
  if (changed) announce();
  return changed;
};

/** The oldest entry in this scope whose request has finished, if any. */
export const offerableSend = (key) => (heldSendsByScope.get(key) || []).find((entry) => !entry.inFlight);

/** Whether this scope has anything to offer, without taking it. */
export const hasOfferableSend = (key) => !!offerableSend(key);

/**
 * Drop everything that does not belong to the signed-in person.
 *
 * Called when the session's identity changes. Deliberately NOT on a change of practice:
 * that is the same person, their entries are keyed by practice already so nothing can
 * cross over, and deleting them would throw away a message that is still in flight for
 * the practice they just left.
 */
export const forgetOtherUsers = (userId) => {
  const mine = `${userId || "anon"}:`;
  [...heldSendsByScope.keys()].forEach((key) => {
    if (!key.startsWith(mine)) heldSendsByScope.delete(key);
  });
};
